import { useCallback, useMemo, useRef, useState, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Loader2, Upload, FileSpreadsheet, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MATCH_LABELS = {
  cf_ospite: "CF ospite",
  cf_familiare: "CF familiare",
  cf_contatto_familiare: "CF contatto familiare",
  nome_ospite: "Nome ospite",
  nome_familiare: "Nome familiare",
};

function toggleModifica(ospiti, fascicoloId, campo, checked) {
  return ospiti.map((o) => {
    if (o.fascicolo_id !== fascicoloId) return o;
    return {
      ...o,
      modifiche: o.modifiche.map((m) =>
        m.campo === campo ? { ...m, selezionato: checked } : m,
      ),
    };
  });
}

function toggleOspite(ospiti, fascicoloId, checked) {
  return ospiti.map((o) => {
    if (o.fascicolo_id !== fascicoloId) return o;
    return {
      ...o,
      modifiche: o.modifiche.map((m) => ({ ...m, selezionato: checked })),
    };
  });
}

export default function ImportClientiFicDialog({
  open,
  onOpenChange,
  headers,
  strutturaIdForConfig,
  onSuccess,
}) {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState("upload");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [fileName, setFileName] = useState("");
  const [anteprima, setAnteprima] = useState(null);
  const [ospiti, setOspiti] = useState([]);
  const [expanded, setExpanded] = useState({});

  const reset = useCallback(() => {
    setStep("upload");
    setLoading(false);
    setApplying(false);
    setFileName("");
    setAnteprima(null);
    setOspiti([]);
    setExpanded({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const selectedCount = useMemo(() => {
    let osp = 0;
    let mods = 0;
    for (const o of ospiti) {
      const sel = o.modifiche.filter((m) => m.selezionato);
      if (sel.length) {
        osp += 1;
        mods += sel.length;
      }
    }
    return { ospiti: osp, modifiche: mods };
  }, [ospiti]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Seleziona un file Excel (.xlsx) export clienti da Fatture in Cloud");
      return;
    }
    setLoading(true);
    setFileName(file.name);
    try {
      const form = new FormData();
      form.append("file", file);
      if (strutturaIdForConfig) form.append("strutturaId", strutturaIdForConfig);
      const res = await axios.post(
        `${API_URL}/api/fatturazione/import-clienti-fic/anteprima`,
        form,
        { headers },
      );
      setAnteprima(res.data);
      setOspiti(res.data.ospiti || []);
      const exp = {};
      for (const o of res.data.ospiti || []) exp[o.fascicolo_id] = true;
      setExpanded(exp);
      setStep("preview");
      const r = res.data.riepilogo || {};
      if (!(r.ospiti_da_aggiornare > 0)) {
        toast.info("Nessuna differenza rilevata rispetto ai dati attuali in Affidya");
      } else {
        toast.success(
          `${r.ospiti_da_aggiornare} ospiti con ${r.modifiche_totali} modifiche proposte`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore lettura file Excel");
      setFileName("");
    } finally {
      setLoading(false);
    }
  };

  const handleApplica = async () => {
    const items = ospiti
      .map((o) => ({
        fascicolo_id: o.fascicolo_id,
        modifiche: o.modifiche
          .filter((m) => m.selezionato)
          .map((m) => ({ campo: m.campo, nuovo: m.nuovo })),
      }))
      .filter((it) => it.modifiche.length > 0);

    if (!items.length) {
      toast.error("Seleziona almeno una modifica da applicare");
      return;
    }

    setApplying(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/import-clienti-fic/applica`,
        { items, strutturaId: strutturaIdForConfig || undefined },
        { headers },
      );
      toast.success(res.data.message || "Anagrafiche aggiornate");
      handleOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'aggiornamento");
    } finally {
      setApplying(false);
    }
  };

  const riepilogo = anteprima?.riepilogo;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left">
            <FileSpreadsheet className="w-5 h-5 text-sky-600 shrink-0" />
            Importa anagrafiche da FIC
          </DialogTitle>
          <DialogDescription className="text-left">
            Carica l&apos;export «Lista clienti» da Fatture in Cloud per confrontare e aggiornare
            indirizzi, email, PEC e codice SDI degli ospiti in Affidya.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4 py-2">
            <label
              className={cn(
                "flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-200",
                "bg-slate-50/80 px-6 py-10 cursor-pointer hover:border-sky-300 hover:bg-sky-50/50 transition-colors",
                loading && "pointer-events-none opacity-60",
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="sr-only"
                disabled={loading}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              {loading ? (
                <Loader2 className="w-10 h-10 text-sky-600 animate-spin" />
              ) : (
                <Upload className="w-10 h-10 text-slate-400" />
              )}
              <div className="text-center">
                <p className="font-medium text-slate-800">
                  {loading ? "Analisi in corso…" : "Clicca per selezionare il file Excel"}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Export da FIC: Anagrafiche → Clienti → Esporta
                </p>
                {fileName && <p className="text-xs text-sky-700 mt-2 font-mono">{fileName}</p>}
              </div>
            </label>
          </div>
        )}

        {step === "preview" && (
          <div className="flex flex-col min-h-0 flex-1 gap-3 overflow-hidden">
            {riepilogo && (
              <div className="flex flex-wrap gap-2 text-xs shrink-0">
                <Badge variant="outline" className="bg-white">
                  {riepilogo.righe_fic} clienti nel file
                </Badge>
                <Badge className="bg-sky-100 text-sky-800">
                  {riepilogo.ospiti_da_aggiornare} ospiti da aggiornare
                </Badge>
                <Badge variant="outline" className="text-slate-600">
                  {riepilogo.fic_non_matchati} righe FIC non collegate
                </Badge>
                <Badge variant="outline" className="text-slate-500">
                  {riepilogo.ospiti_senza_fic} ospiti senza corrispondenza nel file
                </Badge>
              </div>
            )}

            {!ospiti.length ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                Nessuna differenza da applicare: i dati in Affidya coincidono già con FIC.
              </p>
            ) : (
              <div className="overflow-auto flex-1 min-h-0 rounded-lg border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-8" />
                      <TableHead className="text-slate-600">Ospite</TableHead>
                      <TableHead className="text-slate-600">Match</TableHead>
                      <TableHead className="text-slate-600">Modifiche</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ospiti.map((o) => {
                      const isOpen = expanded[o.fascicolo_id];
                      const selCount = o.modifiche.filter((m) => m.selezionato).length;
                      const allSel = selCount === o.modifiche.length;
                      return (
                        <Fragment key={o.fascicolo_id}>
                          <TableRow className="hover:bg-slate-50/80">
                            <TableCell className="p-2">
                              <button
                                type="button"
                                className="p-1 rounded hover:bg-slate-100"
                                onClick={() =>
                                  setExpanded((prev) => ({
                                    ...prev,
                                    [o.fascicolo_id]: !prev[o.fascicolo_id],
                                  }))
                                }
                              >
                                {isOpen ? (
                                  <ChevronDown className="w-4 h-4 text-slate-500" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-500" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-slate-800">{o.ospite}</p>
                              {o.fic_denominazione &&
                                o.fic_denominazione.toUpperCase() !== (o.ospite || "").toUpperCase() && (
                                  <p className="text-xs text-slate-500">FIC: {o.fic_denominazione}</p>
                                )}
                              <p className="text-xs font-mono text-slate-400">{o.codice_fiscale_ospite}</p>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">
                                {MATCH_LABELS[o.match_tipo] || o.match_tipo}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={allSel}
                                  onCheckedChange={(c) =>
                                    setOspiti((prev) => toggleOspite(prev, o.fascicolo_id, !!c))
                                  }
                                />
                                <span className="text-sm text-slate-600">
                                  {selCount}/{o.modifiche.length} campi
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                          {isOpen &&
                            o.modifiche.map((m) => (
                              <TableRow
                                key={`${o.fascicolo_id}-${m.campo}`}
                                className="bg-slate-50/50 text-sm"
                              >
                                <TableCell />
                                <TableCell colSpan={3} className="py-2">
                                  <div className="flex items-start gap-3 pl-6">
                                    <Checkbox
                                      checked={!!m.selezionato}
                                      onCheckedChange={(c) =>
                                        setOspiti((prev) =>
                                          toggleModifica(prev, o.fascicolo_id, m.campo, !!c),
                                        )
                                      }
                                      className="mt-0.5"
                                    />
                                    <div className="min-w-0 flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                                      <span className="font-medium text-slate-700">{m.label}</span>
                                      <span className="text-slate-500 truncate" title={m.attuale}>
                                        {m.attuale || "—"}
                                      </span>
                                      <span className="text-sky-800 truncate font-medium" title={m.nuovo}>
                                        → {m.nuovo}
                                      </span>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {(anteprima?.fic_non_matchati?.length > 0) && (
              <details className="text-xs text-slate-500 shrink-0">
                <summary className="cursor-pointer hover:text-slate-700">
                  {anteprima.fic_non_matchati.length} clienti FIC non collegati (mostra)
                </summary>
                <ul className="mt-2 max-h-24 overflow-auto space-y-1 pl-2">
                  {anteprima.fic_non_matchati.map((r, i) => (
                    <li key={i}>
                      {r.denominazione || "—"}
                      {r.codice_fiscale ? ` · ${r.codice_fiscale}` : ""}
                      <span className="text-slate-400"> — {r.motivo}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0 shrink-0">
          {step === "preview" && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
              }}
              disabled={applying}
            >
              Carica altro file
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={applying}>
            Annulla
          </Button>
          {step === "preview" && ospiti.length > 0 && (
            <Button
              type="button"
              onClick={handleApplica}
              disabled={applying || selectedCount.modifiche === 0}
              className="bg-sky-600 hover:bg-sky-700"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Applica {selectedCount.modifiche} modifiche su {selectedCount.ospiti} ospiti
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
